import React, { useState } from 'react';
import axios from 'axios';
import { Box, Button, CircularProgress, InputAdornment, OutlinedInput, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Brand from '../components/Brand';

const LoginPage = ({ onLogin, message, sessionHours = 4 }) => {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const duration = Number(sessionHours) === 1 ? '1 hour' : `${sessionHours} hours`;

  const submit = async event => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await axios.post('/api/login', { password });
      onLogin(response.data.expiresAt);
    } catch (loginError) {
      const reason = loginError.response?.data?.error;
      setError(reason === 'Wrong password' ? 'Wrong password. Try again.' : reason || 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' }, bgcolor: 'background.default' }}>
      <Box sx={{
        position: 'relative', overflow: 'hidden', bgcolor: '#0e2a2f', color: '#cfe3e1',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        px: { xs: 2.5, md: 7 }, py: { xs: 2.25, md: 5.5 }, minHeight: { xs: 78, md: '100vh' },
        '&:after': { content: '""', position: 'absolute', right: -120, bottom: -120, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,.45), rgba(20,184,166,0) 70%)', display: { xs: 'none', md: 'block' } }
      }}>
        <Brand dark />
        <Box sx={{ position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' }, maxWidth: 500 }}>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 750, mb: 2.25 }}>Excel in, SFCC-ready XML out.</Typography>
          {['Pricebooks, inventory and catalogs', 'Every row checked before you convert', 'Download catalogs as one zip'].map(line => (
            <Box key={line} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.2 }}>
              <CheckOutlinedIcon sx={{ color: '#5eead4', fontSize: 18 }} />
              <Typography variant="body2">{line}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" sx={{ color: '#7fa5a1', position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' } }}>Deraah Trading Company · internal tool</Typography>
      </Box>
      <Box sx={{ display: 'grid', placeItems: 'center', px: { xs: 2.5, sm: 4 }, py: { xs: 3.5, md: 4 } }}>
        <Box sx={{ width: '100%', maxWidth: 380, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: '16px', p: { xs: 3, sm: 3.5 } }}>
          {message && <Box sx={{ p: 1.25, mb: 2, bgcolor: '#fff9db', border: '1px solid #ffec99', borderRadius: '10px', color: '#8a6d00' }}>
            <Typography variant="body2">Your session ended after {duration}. Please sign in again.</Typography>
          </Box>}
          <Typography component="h1" variant="h5" sx={{ fontWeight: 750 }}>Sign in</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>Enter the team password to continue.</Typography>
          <Box component="form" onSubmit={submit}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Password</Typography>
            <OutlinedInput
              autoFocus fullWidth size="small" type={showPassword ? 'text' : 'password'}
              value={password} error={Boolean(error)} onChange={event => setPassword(event.target.value)}
              disabled={submitting} inputProps={{ 'aria-label': 'Password' }}
              endAdornment={<InputAdornment position="end">
                <Button size="small" onClick={() => setShowPassword(value => !value)} sx={{ minWidth: 0, px: 0.75, color: error ? 'error.main' : 'text.secondary', fontSize: 12 }}>
                  {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </InputAdornment>}
              sx={{ mt: 0.5 }}
            />
            {error && <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>{error}</Typography>}
            <Button fullWidth type="submit" variant="contained" disabled={submitting || !password} sx={{ mt: 2, py: 1.1 }} startIcon={submitting ? <CircularProgress size={17} color="inherit" /> : null}>
              Sign in
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 1.75, color: 'text.secondary' }}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">You stay signed in for {duration}.</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
